import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Logo } from '../design-system/components';
import { useAuth } from '../lib/auth';
import { api, type ApiError } from '../lib/api';

interface InviteInfo {
  roomName: string;
  screenName: string;
  status: 'scheduled' | 'open' | 'closed';
}

export function JoinRoom() {
  const { token = '' } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<InviteInfo>(`/api/rooms/invite/${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch(() => setError('This invite link is not valid anymore.'));
  }, [token]);

  async function join() {
    setBusy(true);
    setError(null);
    try {
      const { room } = await api<{ room: { id: string } }>('/api/rooms/join', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      nav(`/chat/${room.id}`);
    } catch (err) {
      const e = err as ApiError;
      setError(e.error === 'room_closed' ? 'This room has closed.' : 'Could not join the room.');
    } finally {
      setBusy(false);
    }
  }

  function goSignIn() {
    // Stash the invite so the kid lands back here after signing in.
    sessionStorage.setItem('kidzim_join', token);
    nav('/kid');
  }

  return (
    <div className="kz-center">
      <Card style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
          <Logo height={40} />
        </div>
        {error && <p className="kz-error-text">{error}</p>}
        {info && (
          <>
            <h3 style={{ marginBottom: 'var(--space-2)' }}>You're invited! 🎉</h3>
            <p className="kz-muted" style={{ marginBottom: 'var(--space-5)' }}>
              Join <strong>{info.roomName}</strong> as <strong>{info.screenName}</strong>.
            </p>
            {user?.role === 'kid' ? (
              <Button full size="lg" onClick={join} disabled={busy || info.status === 'closed'}>
                {info.status === 'closed' ? 'Room closed' : busy ? 'Joining…' : 'Join the chat'}
              </Button>
            ) : (
              <Button full size="lg" onClick={goSignIn}>
                Sign in to join
              </Button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
